import { debounce } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { PredictiveSearchResult } from "@/components/talent/InputSearchListTalent";
import { normalizeForSearch } from "@/utils";

import { useFetch } from "./use-fetch";

const DEFAULT_MINIMUM_CHARACTERS = 3;
const DEBOUNCE_DELAY = 500;

type PredictiveSearchInputProps = {
  value: string;
  onValueChange: (value: string) => void;
  onSubmit: (value: string) => void;
  isDropdownOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  results: PredictiveSearchResult[];
  isLoading: boolean;
  disabled?: boolean;
};

type PredictiveSearchResponse<TData> = { data: TData[] } | TData[];

type UsePredictiveSearchOptions<TData> = {
  queryKey: unknown[];
  fetch: (searchTerm: string) => Promise<PredictiveSearchResponse<TData>>;
  mapResult: (item: TData) => PredictiveSearchResult;
  onSubmit?: (searchTerm: string) => void;
  onClear?: () => void;
  enabled?: boolean;
  disabled?: boolean;
  minimumCharacters?: number;
};

export function usePredictiveSearch<TData>({
  queryKey,
  fetch,
  mapResult,
  onSubmit,
  onClear,
  enabled = true,
  disabled,
  minimumCharacters = DEFAULT_MINIMUM_CHARACTERS,
}: UsePredictiveSearchOptions<TData>) {
  const [value, setValue] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const debouncedSearch = useMemo(
    () => debounce((searchTerm: string) => setDebouncedSearchTerm(normalizeForSearch(searchTerm)), DEBOUNCE_DELAY),
    [],
  );

  useEffect(
    function cancelDebouncedSearchWhenHookUnmounts() {
      return () => debouncedSearch.cancel();
    },
    [debouncedSearch],
  );

  useEffect(
    function clearPredictiveSearchWhenDisabled() {
      if (!disabled) {
        return;
      }

      debouncedSearch.cancel();
      setDebouncedSearchTerm("");
      setIsDropdownOpen(false);
    },
    [debouncedSearch, disabled],
  );

  const handleValueChange = (nextValue: string) => {
    setValue(nextValue);

    if (!nextValue) {
      debouncedSearch.cancel();
      setDebouncedSearchTerm("");
      setIsDropdownOpen(false);
      onClear?.();
      return;
    }

    setIsDropdownOpen(true);
    debouncedSearch(nextValue);
  };

  const normalizedValue = normalizeForSearch(value);
  const hasMinimumCharacters = debouncedSearchTerm.length >= minimumCharacters;
  const isWaitingForSearch = normalizedValue.length >= minimumCharacters && normalizedValue !== debouncedSearchTerm;
  const predictiveSearch = useFetch(() => fetch(debouncedSearchTerm), {
    queryKey: [...queryKey, debouncedSearchTerm],
    includeDepartmentInCache: true,
    enabled: enabled && !disabled && hasMinimumCharacters,
  });

  const isLoading = isWaitingForSearch || predictiveSearch.isLoading;

  const closeDropdown = useCallback(() => setIsDropdownOpen(false), []);

  const results = useMemo(() => {
    const response = predictiveSearch.data;
    const items = Array.isArray(response) ? response : (response?.data ?? []);

    return items.map(mapResult);
  }, [mapResult, predictiveSearch.data]);

  const handleSubmit = (_value: string) => {
    onSubmit?.(normalizeForSearch(value));
    closeDropdown();
  };

  const inputProps: PredictiveSearchInputProps = {
    value,
    onValueChange: handleValueChange,
    onSubmit: handleSubmit,
    isDropdownOpen,
    onOpen: () => setIsDropdownOpen(true),
    onClose: closeDropdown,
    results,
    isLoading,
    disabled,
  };

  return { inputProps, results, isLoading };
}
